import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Mail, Phone, Send } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CalendlyBooking from "@/components/CalendlyBooking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRADES = [
  "Roofing",
  "HVAC",
  "Plumbing",
  "Landscaping",
  "Pressure Washing",
  "Pest Control",
  "Other",
];

const TOPICS = [
  "New website",
  "Missed-call text-back",
  "Online booking",
  "Google reviews",
  "Not sure",
];

const INITIAL_FORM = {
  full_name: "",
  business_name: "",
  trade: "",
  phone: "",
  email: "",
  current_website: "",
  city: "",
  help_topics: [],
  message: "",
  sms_consent: false,
};

const PHONE_RE = /^\+?1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} data-testid={`${id}-text`} className="mt-1.5 text-sm text-red-600">
      {message}
    </p>
  );
}

export default function Contact() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleTopic = (t) =>
    setForm((f) => ({
      ...f,
      help_topics: f.help_topics.includes(t)
        ? f.help_topics.filter((x) => x !== t)
        : [...f.help_topics, t],
    }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required.";
    if (!form.business_name.trim()) e.business_name = "Business name is required.";
    if (!form.trade) e.trade = "Please choose your trade.";
    if (!PHONE_RE.test(form.phone.trim()))
      e.phone = "Enter a valid US phone number, like 317-555-1234.";
    if (!EMAIL_RE.test(form.email.trim()))
      e.email = "Enter a valid email address.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        full_name: form.full_name.trim(),
        business_name: form.business_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        current_website: form.current_website.trim() || null,
        city: form.city.trim() || null,
        message: form.message.trim() || null,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <>
      <Seo
        title="Get a Free Website Preview | Web Pilot"
        description="Tell Web Pilot about your business and get a free website preview plus a short video walkthrough. No cost, no obligation — for Indy area home service pros."
      />

      <section className="bg-navy py-16 sm:py-20" data-testid="contact-hero">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              Contact
            </p>
            <h1
              data-testid="contact-heading"
              className="mt-3 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Get your free website preview.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
              Tell me about your business. I'll build a preview and send you a
              short video. No cost, no obligation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-offwhite py-16 sm:py-20">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          {status === "success" ? (
            <div
              data-testid="contact-form-success"
              className="rounded-xl border border-slate-200/80 bg-white p-8 text-center shadow-sm sm:p-12"
            >
              <CheckCircle2
                className="mx-auto h-12 w-12 text-skyblue"
                aria-hidden="true"
              />
              <h2 className="mt-5 font-display text-2xl font-bold text-navy">
                Thanks. I'll reach out within one business day.
              </h2>
              <p className="mt-3 text-slate-600">
                Your preview request is in. Keep an eye on your phone and inbox.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="contact-form"
              className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8"
            >
              {status === "error" && (
                <div
                  data-testid="contact-form-error-banner"
                  className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  Something went wrong on our end. Please try again, or email{" "}
                  <a
                    href="mailto:zach@webpilot.agency"
                    className="font-semibold underline"
                  >
                    zach@webpilot.agency
                  </a>
                  .
                </div>
              )}

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="full_name" className="text-sm font-semibold text-ink">
                    Full name <span className="text-cta">*</span>
                  </Label>
                  <Input
                    id="full_name"
                    data-testid="contact-input-fullname"
                    className="mt-2 h-12"
                    value={form.full_name}
                    onChange={set("full_name")}
                    autoComplete="name"
                    aria-invalid={!!errors.full_name}
                  />
                  <FieldError id="contact-error-fullname" message={errors.full_name} />
                </div>

                <div>
                  <Label htmlFor="business_name" className="text-sm font-semibold text-ink">
                    Business name <span className="text-cta">*</span>
                  </Label>
                  <Input
                    id="business_name"
                    data-testid="contact-input-business"
                    className="mt-2 h-12"
                    value={form.business_name}
                    onChange={set("business_name")}
                    autoComplete="organization"
                    aria-invalid={!!errors.business_name}
                  />
                  <FieldError id="contact-error-business" message={errors.business_name} />
                </div>
              </div>

              <div className="mt-5">
                <Label className="text-sm font-semibold text-ink">
                  Trade <span className="text-cta">*</span>
                </Label>
                <Select
                  value={form.trade}
                  onValueChange={(v) => setForm((f) => ({ ...f, trade: v }))}
                >
                  <SelectTrigger
                    data-testid="contact-select-trade"
                    className="mt-2 h-12 w-full"
                    aria-invalid={!!errors.trade}
                  >
                    <SelectValue placeholder="Select your trade" />
                  </SelectTrigger>
                  <SelectContent data-testid="contact-select-trade-options">
                    {TRADES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError id="contact-error-trade" message={errors.trade} />
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-ink">
                    Phone <span className="text-cta">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    data-testid="contact-input-phone"
                    className="mt-2 h-12"
                    placeholder="317-555-1234"
                    value={form.phone}
                    onChange={set("phone")}
                    autoComplete="tel"
                    aria-invalid={!!errors.phone}
                  />
                  <FieldError id="contact-error-phone" message={errors.phone} />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-ink">
                    Email <span className="text-cta">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    className="mt-2 h-12"
                    value={form.email}
                    onChange={set("email")}
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                  />
                  <FieldError id="contact-error-email" message={errors.email} />
                </div>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="current_website" className="text-sm font-semibold text-ink">
                    Current website
                  </Label>
                  <Input
                    id="current_website"
                    type="url"
                    data-testid="contact-input-website"
                    className="mt-2 h-12"
                    placeholder="yourcompany.com"
                    value={form.current_website}
                    onChange={set("current_website")}
                  />
                  <p className="mt-1.5 text-xs text-slate-500">
                    Don't have one? Leave blank.
                  </p>
                </div>

                <div>
                  <Label htmlFor="city" className="text-sm font-semibold text-ink">
                    City or service area
                  </Label>
                  <Input
                    id="city"
                    data-testid="contact-input-city"
                    className="mt-2 h-12"
                    placeholder="Indianapolis"
                    value={form.city}
                    onChange={set("city")}
                    autoComplete="address-level2"
                  />
                </div>
              </div>

              <fieldset className="mt-6">
                <legend className="text-sm font-semibold text-ink">
                  What do you want help with?
                </legend>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {TOPICS.map((t) => (
                    <label
                      key={t}
                      className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors hover:border-skyblue/50"
                    >
                      <Checkbox
                        data-testid={`contact-checkbox-topic-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        checked={form.help_topics.includes(t)}
                        onCheckedChange={() => toggleTopic(t)}
                      />
                      {t}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="mt-6">
                <Label htmlFor="message" className="text-sm font-semibold text-ink">
                  Message
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  className="mt-2 min-h-28"
                  placeholder="Anything else I should know?"
                  value={form.message}
                  onChange={set("message")}
                />
              </div>

              <div className="mt-6 rounded-lg border border-slate-200 bg-offwhite p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    data-testid="contact-checkbox-sms-consent"
                    checked={form.sms_consent}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, sms_consent: v === true }))
                    }
                    className="mt-0.5"
                  />
                  <span className="text-xs leading-relaxed text-slate-500">
                    I agree to receive text messages from Web Pilot about my
                    inquiry and services, including follow-ups and appointment
                    reminders. Message frequency varies. Message and data rates
                    may apply. Reply STOP to opt out, HELP for help. Consent is
                    not a condition of purchase. See our{" "}
                    <a href="/privacy" className="text-skyblue underline">
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href="/sms-terms" className="text-skyblue underline">
                      SMS Terms
                    </a>
                    .
                  </span>
                </label>
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={status === "sending"}
                className="mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-cta px-6 py-3 font-display text-base font-bold text-white shadow-sm transition-all duration-200 hover:bg-cta-hover hover:shadow-md disabled:opacity-60"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {status === "sending" ? "Sending…" : "Send Me My Free Preview"}
              </button>
              <p className="mt-3 text-center text-xs text-slate-500">
                No cost. No obligation. No card required.
              </p>
            </form>
          )}

          {/* Prefer to talk? — secondary, below the form */}
          <div
            data-testid="prefer-to-talk-card"
            className="mt-10 rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8"
          >
            <h2 className="font-display text-2xl font-bold text-navy">
              Prefer to talk?
            </h2>
            <p className="mt-2 text-slate-600">
              Skip the form. Grab a time for a 15-minute call, or reach me
              directly.
            </p>
            <CalendlyBooking />

            <div className="mt-6 space-y-3 text-base">
              <p className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-skyblue" aria-hidden="true" />
                <a
                  data-testid="contact-phone-link"
                  href="tel:+13174982575"
                  className="font-semibold text-navy underline decoration-skyblue decoration-2 underline-offset-4 transition-colors hover:text-skyblue"
                >
                  317-498-2575
                </a>
              </p>
              <p className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-skyblue" aria-hidden="true" />
                <a
                  data-testid="contact-email-link"
                  href="mailto:zach@webpilot.agency"
                  className="font-semibold text-navy underline decoration-skyblue decoration-2 underline-offset-4 transition-colors hover:text-skyblue"
                >
                  zach@webpilot.agency
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
